"use client";

import Image from "next/image";
import Link from "next/link";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useTranslations, useLocale } from "next-intl";
import Footer from "@/components/Footer";
import { isScheduleStartAfterToday } from "@/lib/isScheduleStartAfterToday";
import { formatThaiDateDdMmYy, formatThaiMonthYearDdMmYy } from "@/lib/format-thai-date";
import { getLocaleField } from "@/lib/locale-field";

interface Course {
  id: string;
  title: string;
  thaiTitle: string;
  description: string;
  titleEn?: string;
  thaiTitleEn?: string;
  descriptionEn?: string;
  location: string;
  locationCode?: string;
  hsrcCenterId?: string;
  startDate: string;
  endDate: string;
  duration: string;
  price: number;
  capacity: number;
  enrolled: number;
  image: string;
  category: string;
  badge?: string;
  isForKids?: boolean;
}

interface CourseSection {
  id: string;
  title: string;
  thaiTitle: string;
  description: string;
  colorFrom: string;
  colorTo: string;
  courses: Course[];
}

interface CenterListPageProps {
  categories: CourseSection[];
  centers: { id: string; name: string; nameEn?: string | null; code: string; codeE?: string | null }[];
  isLoading?: boolean;
  error?: string | null;
  hideMobileBanner?: boolean;
}

function CourseCard({ course, colorFrom, colorTo, centers }: { course: Course; colorFrom: string; colorTo: string; centers: { id: string; code: string; codeE?: string | null }[] }) {
  const t = useTranslations();
  const locale = useLocale();
  const detailUrl =
    course.badge === "KID" || course.isForKids === true
      ? `/course/detail/forkid?id=${course.id}`
      : `/course/detail?id=${course.id}`;

  return (
    <Link href={detailUrl} className="block bg-transparent rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-shadow cursor-pointer group h-full flex flex-col">
      <div className="relative aspect-[2/1] bg-gray-200 overflow-hidden">
        <Image
          src={course.image}
          alt={getLocaleField(course, locale, "title")}
          fill
          className="object-cover group-hover:scale-105 transition-transform duration-300"
          unoptimized={course.image.startsWith('/uploads/')}
        />
        {course.isForKids === true && (
          <div className="absolute top-0 left-5 z-10 pointer-events-none">
            <Image
              src="/images/icons/kids.svg"
              alt={t("course.courseForKids")}
              width={72}
              height={42}
              className="w-12 sm:w-14 md:w-14 lg:w-16 h-auto"
              unoptimized
            />
          </div>
        )}
        {course.badge && (
          <div className="absolute top-0 left-[20px] z-10">
            <Image
              src="/images/logos/Group 10926.png"
              alt={course.badge}
              width={40}
              height={24}
              className="h-6 w-auto object-contain"
            />
          </div>
        )}
        <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded flex items-center gap-2">
          <Image
            src="/images/icons/icon-center-white.svg"
            alt="Location icon"
            width={12}
            height={12}
            className="w-3 h-3 flex-shrink-0 object-contain"
          />
          <span className="text-white text-xs font-medium">{(() => { const center = centers.find(c => c.id === course.hsrcCenterId); if (center) return locale === "en" && center.codeE ? center.codeE : center.code; return course.location; })()}</span>
        </div>
      </div>

      <div 
        className="pt-5 px-5 pb-5 text-white min-h-[160px] sm:min-h-[180px] lg:min-h-[200px] flex flex-col flex-1"
        style={{ 
          background: `linear-gradient(to bottom right, ${colorFrom}, ${colorTo})` 
        }}
      >
        <h3 className="text-sm font-semibold mb-2">{getLocaleField(course, locale, "title")}</h3>
        
        {getLocaleField(course, locale, "thaiTitle") && (
          <h4 className="text-lg font-semibold mb-2 line-clamp-2 min-h-[48px] leading-7">
            {getLocaleField(course, locale, "thaiTitle")}
          </h4>
        )}
        
        {getLocaleField(course, locale, "description") && (
          <p className="text-sm mb-4 line-clamp-2 opacity-90 text-[11px] sm:text-xs lg:text-sm border-t border-b border-white/20 py-3">{getLocaleField(course, locale, "description")}</p>
        )}

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="flex items-start gap-2 text-[11px] sm:text-xs lg:text-sm">
            <Image
              src="/images/icons/ICON-Calendar.svg"
              alt="Calendar icon"
              width={16}
              height={16}
              className="w-4 h-4 flex-shrink-0 mt-0.5 object-contain"
              unoptimized
            />
            <div className="flex flex-col min-w-0">
              <span className="break-words 2xl:whitespace-nowrap 2xl:text-xs">
                {formatThaiDateDdMmYy(course.startDate, locale)}{course.endDate ? ` - ${formatThaiDateDdMmYy(course.endDate, locale)}` : ''}
              </span>
              {course.duration && (
                <span className="text-sm sm:text-lg lg:text-lg font-semibold">{String(course.duration).replace(/[^0-9]/g, '') || course.duration} {t("course.dayUnit")}</span>
              )}
            </div>
          </div>

          {(course.capacity || course.enrolled !== undefined) && (
            <div className="flex items-start gap-2 text-[11px] sm:text-xs lg:text-sm 2xl:text-xs border-l border-white/30 pl-4">
              <Image
                src="/images/icons/icon-helmet-white.svg"
                alt="Helmet icon"
                width={16}
                height={16}
                className="w-4 h-4 flex-shrink-0 mt-0.5 object-contain"
                unoptimized
              />
              <div className="flex flex-col min-w-0">
                <span>{t("course.bookingLabel")}</span>
                <span className="break-words text-sm sm:text-lg lg:text-lg font-semibold">
                  {course.enrolled}/{course.capacity}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-white/30 mb-4"></div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Image
              src="/images/icons/icon-baht-white.svg"
              alt="Baht icon"
              width={24}
              height={24}
              className="w-6 h-6 object-contain"
              unoptimized
            />
            <span className="text-2xl font-medium">{course.price.toLocaleString()}</span>
          </div>
          
          <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center transition-colors">
            <Image
              src="/images/icons/icon-next.svg"
              alt="Arrow icon"
              width={20}
              height={20}
              className="w-5 h-5 object-contain -rotate-90"
              style={{ filter: 'brightness(0) saturate(100%) invert(11%) sepia(100%) saturate(7471%) hue-rotate(353deg) brightness(90%) contrast(112%)' }}
              unoptimized
            />
          </div>
        </div>
      </div>
    </Link>
  );
}


function CategorySection({
  data,
  centers,
}: {
  data: CourseSection;
  centers: { id: string; code: string; codeE?: string | null }[];
}) {
  const t = useTranslations();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  const GAP = 20;

  const getCardsPerRow = useCallback((): number => {
    if (typeof window === "undefined") return 1;
    const vw = window.innerWidth;
    if (vw >= 1728) return 4;
    if (vw >= 1024) return 3;
    if (vw >= 640) return 2;
    return 1;
  }, []);

  const getActiveSlideFromScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const gap = 20;
    const cardsPerRow = getCardsPerRow();
    const cardW = (el.clientWidth - gap * (cardsPerRow - 1)) / cardsPerRow;
    const slide = Math.round(el.scrollLeft / (cardW + gap));
    setActiveSlide(slide);
  }, [getCardsPerRow]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const observer = new ResizeObserver(() => {
      getActiveSlideFromScroll();
    });
    observer.observe(el);

    return () => observer.disconnect();
  }, [getActiveSlideFromScroll]);

  const [cardsPerRow, setCardsPerRow] = useState(1);

  useEffect(() => {
    const update = () => {
      const vw = window.innerWidth;
      if (vw >= 1728) setCardsPerRow(4);
      else if (vw >= 1024) setCardsPerRow(3);
      else if (vw >= 640) setCardsPerRow(2);
      else setCardsPerRow(1);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const cardWidth = containerWidth > 0 && cardsPerRow > 0
    ? (containerWidth - GAP * (cardsPerRow - 1)) / cardsPerRow
    : 0;
  const showCarouselArrows = data.courses.length > cardsPerRow;
  const totalPages = data.courses.length - cardsPerRow + 1;

  const scrollToSlide = (slideIndex: number) => {
    if (!scrollRef.current) return;
    const targetScroll = slideIndex * (cardWidth + GAP);
    scrollRef.current.scrollTo({ left: targetScroll, behavior: "smooth" });
    setActiveSlide(slideIndex);
  };

  const scroll = (direction: "left" | "right") => {
    const next = direction === "left"
      ? Math.max(0, activeSlide - 1)
      : Math.min(data.courses.length - cardsPerRow, activeSlide + 1);
    scrollToSlide(next);
  };

  return (
    <div className="mb-16">
      <h3 className="text-[32px] font-bold leading-[32px] text-dark mb-2">{data.title}</h3>

      <p className="text-[16px] leading-[26px] text-dark mb-8 max-w-[1420px]">{data.description}</p>

      <div className="relative w-full">
        {showCarouselArrows && (
          <button
            type="button"
            onClick={() => scroll("left")}
            aria-label={t("course.prevCourses")}
            className="absolute left-0 top-1/2 z-10 hidden h-10 w-10 -translate-x-full -translate-y-1/2 items-center justify-center pr-2 text-dark transition-opacity hover:opacity-80 lg:flex lg:pr-3"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        <div
          ref={scrollRef}
          className="flex w-full gap-5 overflow-x-auto scrollbar-hide scroll-smooth pb-4 snap-x snap-mandatory"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          onScroll={getActiveSlideFromScroll}
          onTouchEnd={() => {
            let ticks = 0;
            const maxTicks = 20;
            const poll = () => {
              getActiveSlideFromScroll();
              ticks++;
              if (ticks < maxTicks) {
                requestAnimationFrame(poll);
              }
            };
            requestAnimationFrame(poll);
          }}
        >
          {data.courses.map((course) => (
            <div key={course.id} className="flex-shrink-0 w-full sm:w-[calc(50%-10px)] lg:w-[calc(33.333%-13.333px)] 3xl:w-[calc(25%-15px)] snap-start">
              <CourseCard course={course} colorFrom={data.colorFrom} colorTo={data.colorTo} centers={centers} />
            </div>
          ))}
        </div>

        {showCarouselArrows && (
          <button
            type="button"
            onClick={() => scroll("right")}
            aria-label={t("course.nextCourses")}
            className="absolute right-0 top-1/2 z-10 hidden h-10 w-10 translate-x-full -translate-y-1/2 items-center justify-center pl-2 text-dark transition-opacity hover:opacity-80 lg:flex lg:pl-3"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>

      {showCarouselArrows && totalPages > 1 && (
        <div className="relative flex items-center justify-center mt-4">
          <button
            type="button"
            onClick={() => scroll("left")}
            disabled={activeSlide === 0}
            className={`absolute left-0 flex items-center justify-center transition-opacity text-dark lg:hidden ${
              activeSlide === 0 ? "opacity-30 cursor-not-allowed" : "hover:opacity-70"
            }`}
            aria-label={t("course.prevCourses")}
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="flex items-center gap-2">
            {Array.from({ length: totalPages }, (_, i) => i).map((page) => (
              <button
                key={page}
                type="button"
                onClick={() => scrollToSlide(page)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  activeSlide === page ? "bg-honda-red" : "bg-gray-300 hover:bg-gray-400"
                }`}
                aria-label={`Go to page ${page + 1}`}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={() => scroll("right")}
            disabled={activeSlide >= data.courses.length - cardsPerRow}
            className={`absolute right-0 flex items-center justify-center transition-opacity text-dark lg:hidden ${
              activeSlide >= data.courses.length - cardsPerRow ? "opacity-30 cursor-not-allowed" : "hover:opacity-70"
            }`}
            aria-label={t("course.nextCourses")}
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}




export default function CenterListPage({ categories, centers, isLoading, error, hideMobileBanner = false }: CenterListPageProps) {
  const t = useTranslations();
  const locale = useLocale();
  const effectiveCategories = categories;
  const categoryIds = effectiveCategories.map(cat => cat.id);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(categoryIds.length > 0 ? categoryIds : ["beginner", "bigbike", "dirt"]);
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [selectedCourseType, setSelectedCourseType] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const [isCourseTypeDropdownOpen, setIsCourseTypeDropdownOpen] = useState(false);
  const [isStartMonthOpen, setIsStartMonthOpen] = useState(false);
  const [isEndMonthOpen, setIsEndMonthOpen] = useState(false);
  const startDateRef = useRef<HTMLInputElement>(null);
  const endDateRef = useRef<HTMLInputElement>(null);
  const startMonthRef = useRef<HTMLDivElement>(null);
  const endMonthRef = useRef<HTMLDivElement>(null);
  const locationDropdownRef = useRef<HTMLDivElement>(null);
  const courseTypeDropdownRef = useRef<HTMLDivElement>(null);

  const filteredCategories = effectiveCategories
    .filter(category => {
      if (selectedCourseType !== "all" && category.id !== selectedCourseType) {
        return false;
      }
      return true;
    })
    .map(category => ({
      ...category,
      courses: category.courses.filter(course => {
        if (!isScheduleStartAfterToday(course.startDate)) {
          return false;
        }

        if (selectedLocation !== "all") {
          if (course.hsrcCenterId) {
            if (course.hsrcCenterId !== selectedLocation) return false;
          } else {
            const courseLocationCode = course.locationCode || course.location.toLowerCase().replace(/honda safety riding (center|park)\s*/gi, '').replace(/\s+/g, '-');
            if (courseLocationCode !== selectedLocation) {
              return false;
            }
          }
        }

        if (startDate || endDate) {
          const courseStart = new Date(course.startDate);
          const filterStart = startDate ? new Date(startDate + "-01") : null;
          const filterEnd = endDate ? new Date(endDate + "-01") : null;

          if (filterStart && courseStart < filterStart) {
            return false;
          }
          if (filterEnd) {
            const endOfMonth = new Date(filterEnd.getFullYear(), filterEnd.getMonth() + 1, 0);
            if (courseStart > endOfMonth) {
              return false;
            }
          }
        }

        return true;
      }),
    }))
    .filter(category => category.courses.length > 0);

  const dateRange = useMemo(() => {
    const allCourses = effectiveCategories.flatMap(cat => cat.courses);
    if (allCourses.length === 0) return { months: [] as { value: string; label: string }[] };
    
    const uniqueMonths = new Map<string, string>();
    allCourses.forEach(course => {
      const courseDate = new Date(course.startDate);
      const year = courseDate.getFullYear();
      const month = courseDate.getMonth() + 1;
      const value = `${year}-${String(month).padStart(2, "0")}`;
      const label = formatThaiMonthYearDdMmYy(value, locale);
      uniqueMonths.set(value, label);
    });
    
    return {
      months: Array.from(uniqueMonths.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([value, label]) => ({ value, label }))
    };
  }, [effectiveCategories]);

  const locationOptions = useMemo(() => [
    { value: "all", label: t("center.allLocations") },
    ...centers.map(center => ({ value: center.id, label: locale === 'en' && center.nameEn ? center.nameEn : center.name })),
  ], [centers, t, locale]);

  const courseTypeOptions = [
    { value: "all", label: t("course.allCategories") },
    ...effectiveCategories.map(cat => ({ value: cat.id, label: locale === 'en' && cat.thaiTitle ? cat.thaiTitle : cat.title })),
  ];

  const formatDateDisplay = (dateString: string) => formatThaiMonthYearDdMmYy(dateString, locale);

  const formatDateRangeDisplay = () => {
    if (!startDate && !endDate) return "";
    if (startDate && !endDate) return formatDateDisplay(startDate);
    if (!startDate && endDate) return formatDateDisplay(endDate);
    return `${formatDateDisplay(startDate)} - ${formatDateDisplay(endDate)}`;
  };

  const handleCalendarIconClick = () => {
    setIsDateRangeOpen(!isDateRangeOpen);
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
    if (e.target.value && !endDate) {
      setEndDate(e.target.value);
    }
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isDateRangeOpen && !target.closest('.date-range-container')) {
        setIsDateRangeOpen(false);
      }
      if (isLocationDropdownOpen && locationDropdownRef.current && !locationDropdownRef.current.contains(target)) {
        setIsLocationDropdownOpen(false);
      }
      if (isCourseTypeDropdownOpen && courseTypeDropdownRef.current && !courseTypeDropdownRef.current.contains(target)) {
        setIsCourseTypeDropdownOpen(false);
      }
      if (isStartMonthOpen && startMonthRef.current && !startMonthRef.current.contains(target)) {
        setIsStartMonthOpen(false);
      }
      if (isEndMonthOpen && endMonthRef.current && !endMonthRef.current.contains(target)) {
        setIsEndMonthOpen(false);
      }
    };

    if (isDateRangeOpen || isLocationDropdownOpen || isCourseTypeDropdownOpen || isStartMonthOpen || isEndMonthOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDateRangeOpen, isLocationDropdownOpen, isCourseTypeDropdownOpen, isStartMonthOpen, isEndMonthOpen]);

  return (
    <div className="min-h-screen bg-white font-aphonda">
      

      {hideMobileBanner ? (
        <>
          <section className="lg:hidden bg-white px-6 pt-6 pb-2">
            <div className="max-w-[1920px] 2xl:max-w-[1440px] mx-auto w-full">
              <h1 className="text-[32px] font-bold text-dark leading-[32px]">{t("course.title")}</h1>
            </div>
          </section>

          <section className="hidden lg:block relative w-full overflow-hidden bg-white">
            <div className="relative w-full aspect-[8/1]">
              <Image
                src="/images/cover/cover-coursemc.jpg"
                alt="Course Hero Banner"
                fill
                className="object-cover object-center w-full"
                sizes="100vw"
                priority
              />
              <div className="absolute inset-0 px-6 lg:px-[clamp(2rem,5vw,250px)] flex items-center">
                <div className="max-w-[1920px] 2xl:max-w-[1440px] mx-auto w-full">
                  <h1 className="text-3xl md:text-4xl lg:text-[56px] font-bold text-white leading-tight md:leading-tight lg:leading-[56px]">{t("course.title")}</h1>
                </div>
              </div>
            </div>
          </section>
        </>
      ) : (
        <section className="relative w-full overflow-hidden bg-white">
          <div className="relative w-full aspect-[8/1] md:aspect-[9.6/1] lg:aspect-[8/1]">
            <Image
              src="/images/cover/cover-coursemc.jpg"
              alt="Course Hero Banner"
              fill
              className="object-cover object-center w-full"
              sizes="100vw"
              priority
            />
            <div className="absolute inset-0 px-6 lg:px-[clamp(2rem,5vw,250px)] flex items-center">
              <div className="max-w-[1920px] 2xl:max-w-[1440px] mx-auto w-full">
                <h1 className="text-3xl md:text-4xl lg:text-[56px] font-bold text-white leading-tight md:leading-tight lg:leading-[56px]">{t("course.title")}</h1>
              </div>
            </div>
          </div>
        </section>
      )}

      <div className={`px-6 lg:px-[clamp(2rem,5vw,250px)] pt-[10px] pb-4${hideMobileBanner ? ' hidden lg:block' : ''}`}>
        <div className="max-w-[1920px] 2xl:max-w-[1440px] mx-auto">
          <nav className="flex items-center gap-2 text-xs text-dark">
            <a href="/" className="hover:text-honda-red transition-colors">
              {t("common.home")}
            </a>
            <img src="/images/icons/icon-breadcrumb.svg" alt="" className="w-[6px] h-[11px]" aria-hidden="true" />
            <a href="/course/motorcycle/" className="text-honda-red hover:text-honda-red transition-colors">
              {t("course.title")}
            </a>
          </nav>
        </div>
      </div>

      <div className="bg-white">
        <div className="px-6 lg:px-[clamp(2rem,5vw,250px)] pt-6 pb-0">
          <div className="max-w-[1920px] 2xl:max-w-[1440px] mx-auto">
          <h2 className="text-xl md:text-2xl lg:text-[32px] font-normal leading-tight md:leading-[32px] text-dark mb-4 md:mb-6">{t("course.selectDateRange")}</h2>
          
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <div className="relative" ref={locationDropdownRef}>
              <button
                type="button"
                onClick={() => setIsLocationDropdownOpen(!isLocationDropdownOpen)}
                className="w-full h-[50px] px-4 pr-10 rounded-lg border border-gray-300 text-base text-dark bg-white cursor-pointer hover:border-honda-red transition-colors focus:outline-none focus:ring-2 focus:ring-honda-red text-left flex items-center justify-between relative overflow-hidden"
              >
                <span className="relative z-10">
                  {locationOptions.find(opt => opt.value === selectedLocation)?.label || t("center.allLocations")}
                </span>
                <div className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                  <div className="w-[50px] h-[50px] bg-honda-red flex items-center justify-center rounded">
                    <Image
                      src="/images/icons/icon-next.svg"
                      alt="Dropdown icon"
                      width={24}
                      height={24}
                      className={`w-6 h-6 object-contain transition-transform duration-300 ease-in-out ${isLocationDropdownOpen ? 'rotate-180' : ''}`}
                      style={{ filter: 'brightness(0) saturate(100%) invert(100%)' }}
                      unoptimized
                    />
                  </div>
                </div>
              </button>
              
              <div
                className={`absolute top-full left-0 right-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg z-50 overflow-hidden transition-all duration-300 ease-in-out ${
                  isLocationDropdownOpen
                    ? 'opacity-100 visible translate-y-0'
                    : 'opacity-0 invisible -translate-y-2 pointer-events-none'
                }`}
              >
                {locationOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setSelectedLocation(option.value);
                      setIsLocationDropdownOpen(false);
                    }}
                    className={`w-full px-4 py-3 text-left text-base transition-colors duration-200 ${
                      selectedLocation === option.value
                        ? 'bg-honda-red text-white'
                        : 'text-dark hover:bg-gray-50'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="relative" ref={courseTypeDropdownRef}>
              <button
                type="button"
                onClick={() => setIsCourseTypeDropdownOpen(!isCourseTypeDropdownOpen)}
                className="w-full h-[50px] px-4 pr-10 rounded-lg border border-gray-300 text-base text-dark bg-white cursor-pointer hover:border-honda-red transition-colors focus:outline-none focus:ring-2 focus:ring-honda-red text-left flex items-center justify-between relative overflow-hidden"
              >
                <span className="relative z-10">
                  {courseTypeOptions.find(opt => opt.value === selectedCourseType)?.label || t("course.allCourses")}
                </span>
                <div className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                  <div className="w-[50px] h-[50px] bg-honda-red flex items-center justify-center rounded">
                    <Image
                      src="/images/icons/icon-next.svg"
                      alt="Dropdown icon"
                      width={24}
                      height={24}
                      className={`w-6 h-6 object-contain transition-transform duration-300 ease-in-out ${isCourseTypeDropdownOpen ? 'rotate-180' : ''}`}
                      style={{ filter: 'brightness(0) saturate(100%) invert(100%)' }}
                      unoptimized
                    />
                  </div>
                </div>
              </button>
              
              <div
                className={`absolute top-full left-0 right-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg z-50 overflow-hidden transition-all duration-300 ease-in-out ${
                  isCourseTypeDropdownOpen
                    ? 'opacity-100 visible translate-y-0'
                    : 'opacity-0 invisible -translate-y-2 pointer-events-none'
                }`}
              >
                {courseTypeOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setSelectedCourseType(option.value);
                      setIsCourseTypeDropdownOpen(false);
                    }}
                    className={`w-full px-4 py-3 text-left text-base transition-colors duration-200 ${
                      selectedCourseType === option.value
                        ? 'bg-honda-red text-white'
                        : 'text-dark hover:bg-gray-50'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="relative date-range-container">
              <div 
                className="w-full h-[50px] px-4 pr-10 rounded-lg border border-gray-300 text-base text-dark bg-white cursor-pointer hover:border-honda-red transition-colors flex items-center date-input-english"
                onClick={handleCalendarIconClick}
              >
                <span className="text-base text-dark">
                  {formatDateRangeDisplay() || t("course.dateRangePlaceholder")}
                </span>
              </div>
              
              {isDateRangeOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg p-4 z-50 date-range-container">
                  <div className="grid grid-cols-2 gap-4">
                    <div ref={startMonthRef} className="relative">
                      <label className="block text-sm text-gray-600 mb-2">{t("course.startMonth")}</label>
                      <button
                        type="button"
                        onClick={() => setIsStartMonthOpen(!isStartMonthOpen)}
                        className="w-full h-[40px] px-3 pr-8 rounded border border-gray-300 text-base text-dark bg-white text-left hover:border-honda-red transition-colors focus:outline-none focus:ring-2 focus:ring-honda-red"
                      >
                        {startDate ? dateRange.months.find(m => m.value === startDate)?.label || t("course.select") : t("course.select")}
                      </button>
                      {isStartMonthOpen && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-[200px] overflow-y-auto">
                          {dateRange.months.map((month) => (
                            <button
                              key={month.value}
                              type="button"
                              onClick={() => {
                                setStartDate(month.value);
                                if (!endDate || endDate < month.value) {
                                  setEndDate(month.value);
                                }
                                setIsStartMonthOpen(false);
                              }}
                              className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 ${
                                startDate === month.value ? 'bg-honda-red text-white hover:bg-honda-red' : ''
                              }`}
                            >
                              {month.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div ref={endMonthRef} className="relative">
                      <label className="block text-sm text-gray-600 mb-2">{t("course.endMonth")}</label>
                      <button
                        type="button"
                        onClick={() => setIsEndMonthOpen(!isEndMonthOpen)}
                        className="w-full h-[40px] px-3 pr-8 rounded border border-gray-300 text-base text-dark bg-white text-left hover:border-honda-red transition-colors focus:outline-none focus:ring-2 focus:ring-honda-red"
                      >
                        {endDate ? dateRange.months.find(m => m.value === endDate)?.label || t("course.select") : t("course.select")}
                      </button>
                      {isEndMonthOpen && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-[200px] overflow-y-auto">
                          {dateRange.months
                            .filter(m => !startDate || m.value >= startDate)
                            .map((month) => (
                            <button
                              key={month.value}
                              type="button"
                              onClick={() => {
                                setEndDate(month.value);
                                setIsEndMonthOpen(false);
                              }}
                              className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 ${
                                endDate === month.value ? 'bg-honda-red text-white hover:bg-honda-red' : ''
                              }`}
                            >
                              {month.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-4">
                    <button
                      onClick={() => {
                        setStartDate("");
                        setEndDate("");
                        setIsDateRangeOpen(false);
                      }}
                      className="px-4 py-2 text-base text-dark hover:bg-gray-50 rounded transition-colors"
                    >
                      {t("course.clear")}
                    </button>
                    <button
                      onClick={() => setIsDateRangeOpen(false)}
                      className="px-4 py-2 bg-honda-red text-white rounded hover:bg-honda-red-dark transition-colors"
                    >
                      {t("course.apply")}
                    </button>
                  </div>
                 </div>
              )}
              
              <div 
                onClick={handleCalendarIconClick}
                className="absolute right-0 top-1/2 -translate-y-1/2 w-[50px] h-[50px] rounded bg-honda-red flex items-center justify-center cursor-pointer hover:bg-honda-red-dark transition-colors z-10"
              >
                <Image
                  src="/images/icons/ICON-Calendar.svg"
                  alt="Calendar icon"
                  width={32}
                  height={32}
                  className="w-8 h-8 object-contain filter brightness-0 invert"
                />
              </div>
            </div>
          </div>

          <p className="text-xs md:text-[12px] leading-tight md:leading-[12px] text-dark mt-6 md:mt-8 mb-4">
            {t("course.searchResultPrefix")} {locationOptions.find(opt => opt.value === selectedLocation)?.label || t("center.allLocations")} / {courseTypeOptions.find(opt => opt.value === selectedCourseType)?.label || t("course.allCourses")}{startDate || endDate ? ` / ${formatDateRangeDisplay()}` : ''}
          </p>

          <div className="border-t-[2px] md:border-t-[3px] lg:border-t-[4px] border-honda-red w-full"></div>
          </div>
        </div>
      </div>

      <main className="bg-white pt-12 pb-4">
        <div className="px-6 lg:px-[clamp(2rem,5vw,250px)]">
          <div className="max-w-[1920px] 2xl:max-w-[1440px] mx-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-honda-red"></div>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-red-600">{error}</p>
            </div>
          ) : filteredCategories.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-600">{t("course.noCourses")}</p>
            </div>
          ) : (
            filteredCategories.map((category) =>
              selectedCategories.includes(category.id) ? (
                <CategorySection key={category.id} data={category} centers={centers} />
              ) : null
            )
          )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
